import type {
  PlanCode,
  PlanDocument,
  UsageDocument,
  UsageLimitKey,
} from '@/lib/types/subscription'
import {
  PLAN_CODES,
  PURCHASABLE_PLAN_CODES,
  USAGE_LIMITS,
} from '@/lib/types/subscription'
import { checkUsageLimit } from '@/lib/types/subscriptionHelpers'

/**
 * Pure presentation helpers for Plan & billing (design 15c). Every number
 * shown on the page is derived here from the plan catalogue (seeded prices
 * and limits) and the synced usage document — nothing is hard-coded.
 */

/** Tier order for upgrade/downgrade wording. Legacy Team AI ranks with Team. */
export const PLAN_TIER_RANK: Record<string, number> = {
  [PLAN_CODES.FREE]: 0,
  [PLAN_CODES.INDIVIDUAL]: 1,
  [PLAN_CODES.TEAM]: 2,
  [PLAN_CODES.AI]: 2,
}

/** Currency from cents; cents only when the amount has them ($3.49, not $3.00). */
export function formatPlanPrice(cents: number, currency = 'USD'): string {
  const amount = cents / 100
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/** Compact binary size: 5242880 → "5MB", 1073741824 → "1GB". */
export function formatCompactBytes(bytes: number): string {
  if (bytes <= 0) return '0MB'
  const gb = bytes / 1024 ** 3
  if (gb >= 1) return `${Number(gb.toFixed(1))}GB`
  const mb = bytes / 1024 ** 2
  if (mb >= 1) return `${Number(mb.toFixed(mb < 10 ? 1 : 0))}MB`
  const kb = bytes / 1024
  return `${Math.max(1, Math.round(kb))}KB`
}

/** Whole-percent saving of paying yearly instead of 12 × monthly. */
export function annualSavingsPercent(plan: PlanDocument): number {
  if (plan.priceMonthly <= 0 || plan.priceAnnual <= 0) return 0
  const yearAtMonthly = plan.priceMonthly * 12
  return Math.max(0, Math.round(((yearAtMonthly - plan.priceAnnual) / yearAtMonthly) * 100))
}

/** "Save up to N%" across the plans on offer. */
export function maxAnnualSavingsPercent(plans: Array<PlanDocument>): number {
  return plans.reduce((max, p) => Math.max(max, annualSavingsPercent(p)), 0)
}

/** Per-month headline price for the chosen interval (yearly ÷ 12). */
export function perMonthCents(plan: PlanDocument, annual: boolean): number {
  return annual ? plan.priceAnnual / 12 : plan.priceMonthly
}

/** Paid plans that can be bought or trialed today, in catalogue order. */
export function purchasablePlans(plans: Array<PlanDocument>): Array<PlanDocument> {
  return plans
    .filter((p) => (PURCHASABLE_PLAN_CODES as ReadonlyArray<string>).includes(p.code))
    .sort((a, b) => a.sortOrder - b.sortOrder)
}

/** The plan has a non-zero (or unlimited) AI budget. Mirrors usePlanFundsAi. */
export function planFundsAi(plan: PlanDocument): boolean {
  const funds = (v: number | undefined) => v === -1 || (typeof v === 'number' && v > 0)
  return funds(plan.limits.aiChatTokensMonthly) || funds(plan.limits.aiEmbeddingTokensMonthly)
}

/** The usage dimensions shown as bars on the current-plan card. */
export const USAGE_BAR_KEYS = [
  USAGE_LIMITS.TASKS,
  USAGE_LIMITS.PROJECTS,
  USAGE_LIMITS.MEMBERS,
  USAGE_LIMITS.STORAGE,
] as const
export type UsageBarKey = (typeof USAGE_BAR_KEYS)[number]

/** Bars turn to the accent colour from here. Matches checkUsageLimit's warning threshold. */
export const NEAR_LIMIT_PERCENT = 80

export type UsageBar = {
  key: UsageBarKey
  used: number
  limit: number
  unlimited: boolean
  percent: number
  near: boolean
  over: boolean
}

export function usageBars(
  plan: PlanDocument | null,
  usage: UsageDocument | null,
): Array<UsageBar> {
  if (!plan) return []
  return USAGE_BAR_KEYS.map((key) => {
    const r = checkUsageLimit(plan, usage, key)
    const percent = r.isUnlimited ? 0 : Math.min(100, r.percentUsed)
    return {
      key,
      used: r.currentUsage,
      limit: r.limit,
      unlimited: r.isUnlimited,
      percent,
      over: !r.isUnlimited && r.isOverLimit,
      near: !r.isUnlimited && r.percentUsed >= NEAR_LIMIT_PERCENT,
    }
  })
}

/** The bar that most needs a warning line: over first, then the fullest near one. */
export function mostPressingBar(bars: Array<UsageBar>): UsageBar | null {
  const flagged = bars.filter((b) => b.near || b.over)
  if (flagged.length === 0) return null
  return [...flagged].sort((a, b) => Number(b.over) - Number(a.over) || b.percent - a.percent)[0] ?? null
}

/**
 * Cheapest purchasable plan that lifts the given limit: removes it (-1) or
 * raises it above the current plan's. Null when no plan on offer does.
 */
export function planThatLifts(
  plans: Array<PlanDocument>,
  key: UsageLimitKey,
  currentLimit: number,
): { plan: PlanDocument; removes: boolean } | null {
  for (const p of purchasablePlans(plans)) {
    const limit = p.limits[key] ?? -1
    if (limit === -1) return { plan: p, removes: true }
    if (limit > currentLimit) return { plan: p, removes: false }
  }
  return null
}

export type CtaIntent =
  | { kind: 'current' }
  | { kind: 'getStarted' }
  | { kind: 'downgradeFree' }
  | { kind: 'switch' }
  | { kind: 'upgrade' }
  | { kind: 'downgrade' }
  | { kind: 'trial' }

/**
 * Which CTA a plan card shows, given where the workspace is today. Same
 * rules the previous PlanCard used: no trial wording once the workspace
 * pays, and never promise a trial the backend won't grant.
 */
export function ctaIntent({
  target,
  isCurrentPlan,
  currentPlanCode,
  trialEligible,
}: {
  target: PlanDocument
  isCurrentPlan: boolean
  currentPlanCode: PlanCode | null
  trialEligible: boolean
}): CtaIntent {
  if (isCurrentPlan) return { kind: 'current' }
  const onPaidPlan = currentPlanCode != null && currentPlanCode !== PLAN_CODES.FREE
  if (target.code === PLAN_CODES.FREE) {
    return onPaidPlan ? { kind: 'downgradeFree' } : { kind: 'getStarted' }
  }
  if (onPaidPlan) {
    const currentRank = PLAN_TIER_RANK[currentPlanCode] ?? 0
    const targetRank = PLAN_TIER_RANK[target.code] ?? 0
    if (targetRank === currentRank) return { kind: 'switch' }
    return targetRank > currentRank ? { kind: 'upgrade' } : { kind: 'downgrade' }
  }
  return trialEligible ? { kind: 'trial' } : { kind: 'upgrade' }
}
